// src/gestion-logistics/hooks/useAccountMasters.ts
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { message } from "antd";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { AccountMaster, AccountType } from "../types/costing.types";

interface UseAccountMastersResult {
  accountMasters: AccountMaster[];
  accountTypes: AccountType[];
  loading: boolean;
  fetchAccountMasters: () => Promise<void>;
  fetchAccountTypes: () => Promise<void>;
  createAccountMaster: (data: Partial<AccountMaster>) => Promise<AccountMaster | null>;
  updateAccountMaster: (id: number, data: Partial<AccountMaster>) => Promise<boolean>;
  deleteAccountMaster: (id: number) => Promise<boolean>;
  toggleAccountMasterStatus: (id: number) => Promise<boolean>;
  getAccountMasterById: (id: number) => Promise<AccountMaster | null>;
}

export const useAccountMasters = (): UseAccountMastersResult => {
  const { selectedCompany } = useSelection();
  const [accountMasters, setAccountMasters] = useState<AccountMaster[]>([]);
  const [accountTypes, setAccountTypes] = useState<AccountType[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAccountTypes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/account-masters/types/all`,
        {
          params: { company_id: selectedCompany }, // Envoyer le company_id sélectionné
        }
      );

      if (response.data.success) {
        setAccountTypes(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[LOGISTICS] Error fetching account types:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des types de comptes"
      );
    }
  }, [selectedCompany]);

  const fetchAccountMasters = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/account-masters`,
        {
          params: { company_id: selectedCompany }, // Envoyer le company_id sélectionné
        }
      );

      if (response.data.success) {
        setAccountMasters(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[LOGISTICS] Error fetching account masters:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des rubriques comptables"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  const getAccountMasterById = useCallback(
    async (id: number): Promise<AccountMaster | null> => {
      try {
        const response = await axios.get(
          `${API_CONFIG.API_URL}/logistics/account-masters/${id}`
        );

        if (response.data.success) {
          return response.data.data;
        }
        return null;
      } catch (error: any) {
        console.error("[LOGISTICS] Error fetching account master:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement de la rubrique"
        );
        return null;
      }
    },
    []
  );

  const createAccountMaster = useCallback(
    async (data: Partial<AccountMaster>): Promise<AccountMaster | null> => {
      try {
        // Ajouter company_id depuis le SelectionContext
        const payload = {
          ...data,
          company_id: selectedCompany
        };

        console.log('[LOGISTICS] Creating account master with company_id:', selectedCompany);

        const response = await axios.post(
          `${API_CONFIG.API_URL}/logistics/account-masters`,
          payload
        );

        if (response.data.success) {
          message.success("Rubrique créée avec succès");
          await fetchAccountMasters();
          return response.data.data;
        }
        return null;
      } catch (error: any) {
        console.error("[LOGISTICS] Error creating account master:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la création de la rubrique"
        );
        return null;
      }
    },
    [fetchAccountMasters, selectedCompany]
  );

  const updateAccountMaster = useCallback(
    async (id: number, data: Partial<AccountMaster>): Promise<boolean> => {
      try {
        // Ajouter company_id depuis le SelectionContext
        const payload = {
          ...data,
          company_id: selectedCompany
        };

        console.log('[LOGISTICS] Updating account master with company_id:', selectedCompany);

        const response = await axios.put(
          `${API_CONFIG.API_URL}/logistics/account-masters/${id}`,
          payload
        );

        if (response.data.success) {
          message.success("Rubrique mise à jour avec succès");
          await fetchAccountMasters();
          return true;
        }
        return false;
      } catch (error: any) {
        console.error("[LOGISTICS] Error updating account master:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la mise à jour de la rubrique"
        );
        return false;
      }
    },
    [fetchAccountMasters, selectedCompany]
  );

  const deleteAccountMaster = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        const response = await axios.delete(
          `${API_CONFIG.API_URL}/logistics/account-masters/${id}`
        );

        if (response.data.success) {
          message.success("Rubrique supprimée avec succès");
          await fetchAccountMasters();
          return true;
        }
        return false;
      } catch (error: any) {
        console.error("[LOGISTICS] Error deleting account master:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la suppression de la rubrique"
        );
        return false;
      }
    },
    [fetchAccountMasters]
  );

  const toggleAccountMasterStatus = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        // Trouver l'account master pour connaître son statut actuel
        const accountMaster = accountMasters.find((am) => am.id === id);
        if (!accountMaster) {
          message.error("Rubrique introuvable");
          return false;
        }

        const newStatus = !accountMaster.is_active;

        const response = await axios.put(
          `${API_CONFIG.API_URL}/logistics/account-masters/${id}`,
          { is_active: newStatus }
        );

        if (response.data.success) {
          message.success(
            newStatus
              ? "Rubrique activée avec succès"
              : "Rubrique désactivée avec succès"
          );
          await fetchAccountMasters();
          return true;
        }
        return false;
      } catch (error: any) {
        console.error("[LOGISTICS] Error toggling account master status:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du changement de statut"
        );
        return false;
      }
    },
    [accountMasters, fetchAccountMasters]
  );

  // Recharger les rubriques comptables automatiquement quand selectedCompany change
  useEffect(() => {
    if (selectedCompany) {
      fetchAccountTypes();
      fetchAccountMasters();
    } else {
      setAccountMasters([]);
      setAccountTypes([]);
    }
  }, [selectedCompany, fetchAccountTypes, fetchAccountMasters]);

  return {
    accountMasters,
    accountTypes,
    loading,
    fetchAccountMasters,
    fetchAccountTypes,
    createAccountMaster,
    updateAccountMaster,
    deleteAccountMaster,
    toggleAccountMasterStatus,
    getAccountMasterById,
  };
};
